import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ScopeT, UserRole } from 'business/definitions';
import { useSelector } from 'store/hooks';
import { useIsAdminAndNotSupportUser } from 'hooks/user';
import { ScopeFormStep } from '../ScopeFormStep/ScopeFormStep';
import PersonalInformationSection from '../UserFormGeneralSection/components/PersonalInformationSection';
import { SingleUserRolesAccessSection } from '../UserFormGeneralSection/components/RolesAccessSection';
import {
    getTooltipMessageForDisabledScope,
    shouldDisplayScope,
} from '../ScopeFormStep/helpers/scopes';
import { useUserCreationFormContext } from '../../CreateUserContext/CreateUserFormContext';
import {
    useValidateUserPersonalInformation,
    useValidateUserSupportRole,
} from '../../hooks/useValidateUserForm';

export type SelectableItemT = {
    component: ReactNode;
    disabled: boolean;
    title: string;
    tooltipMessage?: string;
    warningMessage?: string;
};

type MenuSectionT = {
    title: string;
    children: SelectableItemT[];
};

const ADMIN_SELF_UPDATE_TOOLTIP_MESSAGE =
    'adminSpace.editUser.modal.sideBar.disabledTooltip.adminSelfUpdate';

const getDisableTooltipMessage = (
    scope: ScopeT,
    shouldBlockAdminSelfUpdate: boolean,
    isScopeDisabled: boolean,
) => {
    if (shouldBlockAdminSelfUpdate) return ADMIN_SELF_UPDATE_TOOLTIP_MESSAGE;
    return isScopeDisabled
        ? getTooltipMessageForDisabledScope(scope)
        : undefined;
};

const createScopeChildItem = (
    scope: ScopeT,
    roles: UserRole[],
    shouldBlockAdminSelfUpdate: boolean,
): SelectableItemT => {
    const isScopeDisabled = !shouldDisplayScope(scope, roles);
    return {
        component: ScopeFormStep({ scope }),
        disabled: shouldBlockAdminSelfUpdate || isScopeDisabled,
        tooltipMessage: getDisableTooltipMessage(
            scope,
            shouldBlockAdminSelfUpdate,
            isScopeDisabled,
        ),
        title: scope.label,
    };
};

const useShouldBlockAdminSelfUpdate = (userId: number) => {
    const { id: currentUserId } = useSelector((state) => state.vision.user);
    const isAdminAndNotSupportUser = useIsAdminAndNotSupportUser();
    return userId === currentUserId && isAdminAndNotSupportUser;
};

export const useGetSideMenuSections = (
    existingUsersEmails: string[],
    scopes: ScopeT[],
    userId: number,
): MenuSectionT[] => {
    const { t } = useTranslation();
    const userCreationForm = useUserCreationFormContext();
    const shouldBlockAdminSelfUpdate = useShouldBlockAdminSelfUpdate(userId);
    const generalMenuSection: MenuSectionT = {
        title: t('adminSpace.editUser.modal.sideBar.generalSection.title'),
        children: [
            {
                component: PersonalInformationSection(),
                disabled: false,
                title: t(
                    'adminSpace.editUser.modal.sideBar.generalSection.informationSection',
                ),
                warningMessage: useValidateUserPersonalInformation(
                    userCreationForm,
                    existingUsersEmails,
                ),
            },
            {
                component: SingleUserRolesAccessSection(),
                disabled: shouldBlockAdminSelfUpdate,
                tooltipMessage: shouldBlockAdminSelfUpdate
                    ? ADMIN_SELF_UPDATE_TOOLTIP_MESSAGE
                    : undefined,
                title: t(
                    'adminSpace.editUser.modal.sideBar.generalSection.moduleAccessSection',
                ),
                warningMessage: useValidateUserSupportRole(userCreationForm),
            },
        ],
    };
    const modulesAccessMenuSection: MenuSectionT = {
        title: t('adminSpace.editUser.modal.sideBar.dataAccessSection'),
        children: scopes.map((scope) =>
            createScopeChildItem(
                scope,
                userCreationForm.roles,
                shouldBlockAdminSelfUpdate,
            ),
        ),
    };
    return [generalMenuSection, modulesAccessMenuSection];
};
